<template>
    <div class="cms-content-header-with-image-and-business-search-filters__search-box--container">
        <h1 class="cms-content-header-with-image-and-business-search-filters__search-box--title">
            {{ title || 'Search business offers' }}
        </h1>

        <form action="/business/offers" class="cms-content-header-with-image-and-business-search-filters__search-box--filters">
            <div class="form-group relative">
                <select :disabled="loading" class="form-control form-control--select homepage-hero__select text-capitalize" name="type" id="type" autocomplete="off" v-model="selectedFilters.type">
                    <option :value="null" selected>Select a vehicle type</option>
                    <option class="" v-for="type in initialFilters.type" :value="type" :selected="initialFilters.type && initialFilters.type.length === 1"> {{ type }} </option>
                </select>
                <loading-spinner :overlay="true" v-if="loading" />
            </div>

            <div class="form-group relative">
                <select :disabled="loading" class="form-control form-control--select homepage-hero__select" name="manufacturer" id="manufacturer" autocomplete="off" v-model="selectedFilters.manufacturer">
                    <option :value="null" selected>Select a manufacturer</option>
                    <option v-for="manufacturer in sortedManufacturerOptions" :value="manufacturer.value"> {{ manufacturer.label }} </option>
                </select>
                <loading-spinner :overlay="true" v-if="loading" />
            </div>

            <div class="form-group relative">
                <select :disabled="loading" class="form-control form-control--select homepage-hero__select" name="model" id="model" autocomplete="off" v-model="selectedFilters.model">
                    <option :value="null" selected>Select a model</option>
                    <option v-for="model in scopedFilters.model" :value="model"> {{ model }} </option>
                </select>
                <loading-spinner :overlay="true" v-if="loading" />
            </div>

            <div class="form-group relative">
                <select :disabled="loading" class="form-control form-control--select homepage-hero__select" name="maxPayment" id="maxPayment" autocomplete="off" v-model="selectedFilters.maxPayment">
                    <option :value="null" selected>Select a Monthly Payment</option>
                    <option v-for="option in paymentOptions" :key="option.value" :value="String(option.value)">{{ option.label }}</option>
                </select>
                <loading-spinner :overlay="true" v-if="loading" />
            </div>

            <button type="submit" class="btn btn--search btn--icon">
                <span> Search </span>
                <img src="/images/homepage/multi-brand/search-icon.png" class="btn__icon" alt="" />
            </button>
        </form>
    </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import debounce from 'lodash/debounce';
import getNiceManufacturerName from '../../../core/modules/get-nice-manufacturer-name';
import numberFormat from '../../../core/modules/number-format';
import LoadingSpinner from '../../../core/components/LoadingSpinner.vue';

export default {
    components: {
        LoadingSpinner,
    },
    props: {
        title: {
            type: String,
            required: false,
        },
    },
    data() {
        return {
            selectedFilters: {
                type: 'car',
                manufacturer: null,
                model: null,
                maxPayment: null,
            },
        };
    },
    computed: {
        ...mapState(['initialFilters', 'scopedFilters', 'activeFilters', 'loading']),
        manufacturerOptions() {
            if (this.scopedFilters.manufacturer) {
                return this.scopedFilters.manufacturer.map((manufacturer) => ({
                    label: getNiceManufacturerName(manufacturer),
                    value: manufacturer,
                }));
            }

            if (! this.initialFilters.manufacturer) {
                return [];
            }

            return this.initialFilters.manufacturer.map((manufacturer) => ({
                label: getNiceManufacturerName(manufacturer),
                value: manufacturer,
            }));
        },
        sortedManufacturerOptions() {
            return this.manufacturerOptions.sort((a, b) => {
                if (a.label < b.label) return -1;
                if (a.label > b.label) return 1;
                return 0;
            });
        },
        paymentOptions() {
            const defaultNumbers = [100, 200, 300, 400, 500, 1000, 1500, 2000];

            const minRaw = this.scopedFilters && this.scopedFilters.minMonthlyPayment ? Number(this.scopedFilters.minMonthlyPayment) : null;
            const maxRaw = this.scopedFilters && this.scopedFilters.maxMonthlyPayment ? Number(this.scopedFilters.maxMonthlyPayment) : null;

            const makeOptionsFromNumbers = (arr) => arr.map((n) => ({ value: n, label: 'Under £' + numberFormat(n, 0) }));

            if (!minRaw || !maxRaw || Number.isNaN(minRaw) || Number.isNaN(maxRaw) || maxRaw <= 0) {
                return makeOptionsFromNumbers(defaultNumbers);
            }

            const minRounded = Math.ceil(minRaw / 100) * 100;
            const maxRounded = Math.ceil(maxRaw / 100) * 100;

            if (maxRounded < minRounded) {
                return makeOptionsFromNumbers(defaultNumbers);
            }

            const opts = [];
            for (let v = minRounded; v <= maxRounded; v += 100) {
                opts.push(v);
            }

            return makeOptionsFromNumbers(opts);
        },
    },
    methods: {
        ...mapActions(['getInitialFilters', 'getScopedFilters', 'search']),
        ...mapMutations(['setInitialFilters', 'setScopedFilters', 'setActiveFilters']),
        updateSelectedFilters(key, value) {
            this.$set(this.selectedFilters, key, value);
        },
        getQueryParameter(key) {
            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.get(key);
        }
    },
    watch: {
        selectedFilters: {
            handler: debounce(function () {
                this.setActiveFilters(this.selectedFilters);
                this.getScopedFilters()
                    .then(() => {
                        if (!this.scopedFilters.model.includes(this.selectedFilters.model)) {
                            this.selectedFilters.model = null;
                        }
                    });
            }, 500),
            deep: true,
        },
    },
    created() {
        this.getInitialFilters()
            .then(() => Object.keys(this.selectedFilters)
                .forEach((key) => {
                    const value = this.getQueryParameter(key);

                    if (value !== null && value !== undefined) {
                        this.selectedFilters[key] = value;
                    }
                })
            );
    },
}
</script>

<style>
.text-capitalize {
    text-transform: capitalize;
}
</style>